/**
 * Goods Section
 * HS Code (autocomplete), Quantity, Value, Currency
 */

import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Button } from '@/components/base/buttons/button';
import { XCircle } from '@untitledui/icons';
import type { ContainerFormData, SelectOption } from '@/hooks/useContainerForm';
import { useTranslation } from 'react-i18next';

interface GoodsSectionProps {
    formData: ContainerFormData;
    onInputChange: (field: keyof ContainerFormData, value: string) => void;
    onHsCodeSelect: (code: string) => void;
    hsCodeSearch: string;
    setHsCodeSearch: (value: string) => void;
    hsCodeOptions: SelectOption[];
    quantityUnitOptions: SelectOption[];
    currencyOptions: SelectOption[];
}

export function GoodsSection({
    formData,
    onInputChange,
    onHsCodeSelect,
    hsCodeSearch,
    setHsCodeSearch,
    hsCodeOptions,
    quantityUnitOptions,
    currencyOptions,
}: GoodsSectionProps) {
    const { t } = useTranslation('pages:containers');

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.goods', 'Goods')}</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* HS Code Autocomplete */}
                <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                        {t('form.fields.hsCode', 'HS Code')}
                    </label>
                    {formData.hsCodePrimary ? (
                        <div className="flex items-center gap-2">
                            <div className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary truncate">
                                {formData.hsCodeDisplay}
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                color="secondary"
                                onClick={() => {
                                    onInputChange('hsCodePrimary', '');
                                    onInputChange('hsCodeDisplay', '');
                                    setHsCodeSearch('');
                                }}
                            >
                                <XCircle className="size-4" />
                            </Button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Input
                                placeholder={t('form.fields.hsCodePlaceholder', 'Search by code or description...')}
                                value={hsCodeSearch}
                                onChange={setHsCodeSearch}
                                size="md"
                                aria-label={t('form.fields.hsCode', 'HS Code')}
                            />
                            {hsCodeOptions.length > 0 && hsCodeSearch.length >= 2 && (
                                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                                    {hsCodeOptions.map(hs => (
                                        <button
                                            key={hs.id}
                                            type="button"
                                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary truncate"
                                            onClick={() => onHsCodeSelect(hs.id)}
                                        >
                                            {hs.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Quantity */}
                <div className="grid grid-cols-2 gap-4">
                    <Input
                        label={t('form.fields.quantity', 'Quantity')}
                        type="number"
                        placeholder="0"
                        value={formData.quantityValue}
                        onChange={(value) => onInputChange('quantityValue', value)}
                        size="md"
                    />
                    <Select
                        label={t('form.fields.unit', 'Unit')}
                        items={quantityUnitOptions}
                        selectedKey={formData.quantityUnit}
                        onSelectionChange={(key) => onInputChange('quantityUnit', key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Value (now part of Goods section) */}
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                    label={t('form.fields.amount')}
                    type="number"
                    placeholder={t('form.fields.amountPlaceholder')}
                    value={formData.valueAmount}
                    onChange={(value) => onInputChange('valueAmount', value)}
                    size="md"
                />
                <Select
                    label={t('form.fields.currency')}
                    placeholder={t('form.fields.currencyPlaceholder')}
                    items={currencyOptions}
                    selectedKey={formData.valueCurrency}
                    onSelectionChange={(key) => onInputChange('valueCurrency', key as string)}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>
        </div>
    );
}
